import { useEffect } from "react";
import { useProductData } from "@/zustand/productStore.js";
import { useTemplateStore } from "@/zustand/templateStore.js";
import { useMarketingStore } from "@/zustand/emailMarketingStore.js";
import Bars from "@/components/Bars.jsx";
import getCookie from "@/func/getCookie.js";
import showProductHero from "@/func/showProductHero.js";
import Overlay from "@/components/misc/overlay.jsx";
import Header from "@/components/header";
import { Form } from "@/components/forms/form.jsx";
import BenefitsListV2 from "@/components/BenefitsListV2";
import TestimonialsList from "@/components/testimonials/TestimonialsList";
import SimpleFooter from "@/components/simpleFooter";
import MobileSectionSeperator from "@/components/mobileComponents/MobileSectionSeperator";
import UpsellPopupOverlay from "@/components/popups/upsellPopupOverlay.jsx";
import VariantsPickerSeparate from "@/components/ProductPackages/VariantsPickerSeparate";
import ProductPackages from "@/components/ProductPackages/ProductPackages";
import ProductHeroSection from "@/components/ProductHeroContainer/ProductHeroSection";
import TopbarOffer from "@/components/TopbarOffer";
import OrderOverviewDrawerV3 from "@/components/orderSummary/OrderOverviewDrawerV3.jsx";
import FreeShippingPopup from "@/components/popups/FreeShippingPopup.jsx";
import UpsellPopup from "@/components/popups/UpsellPopup.jsx";
import { useInitializeDefaultProduct } from "@/func/useInitializeDefaultProduct.js";
import Quiz from "@/components/Quiz/Quiz";
import MoneyBackBadge from "@/components/MoneyBackBadge";
import { useQuizModalStore } from "@/zustand/quizModalStore.js";
import PremiumShippingUpsell from "@/components/PremiumShippingUpsell/PremiumShippingUpsell";
import { useEverflowConversion } from "@commerce-core/react-components";

const Layout = () => {
  useInitializeDefaultProduct();

  useEverflowConversion({
    adv_event_id: 4,
    transaction_id: getCookie("ef_transaction_id") ?? "",
    offer_id: getCookie("ef_offer_id") ?? "",
    triggerType: "focused",
    triggerValue: "address",
  });
  const {
    isLoading,
    paymentMethod,
    upsellPopup,
    setLoadingState,
    withProductPicker,
    complianceDesktop,
    complianceMobile,
    isMobile,
    setIsMobile,
    setIsMobileGallery,
  } = useTemplateStore();
  const { freeShippingApplied, freeShippingEnabled } = useMarketingStore();
  const { isQuizModalOpen, closeQuizModal, openQuizModalWithTimeout } = useQuizModalStore();
  const productData = useProductData();

  const base = window.post_template;
  const timeoutBeforeQuiz = base.checkout_v4_quiz?.quiz_timeout_before_quiz_modal || 2000;
  const orderSummaryVersionV2 = base.checkout_v4_summary?.checkout_v4_summary_version;
  const orderSummaryPlacement = base.checkout_v4_summary?.checkout_v4_summary_placement;
  const productPickerV2 = productData?.show_product_picker_v2;
  const complianceMode = (isMobile == false && complianceDesktop) || (isMobile == true && complianceMobile);
  const showMoneyBackBadge = base.checkout_v4_moneyback_badge?.checkout_v4_moneyback_badge_show_badge;
  const showPremiumShipping = base.checkout_v4_premium_shipping?.checkout_v4_premium_shipping_show;
  const isProductHeroVisible = showProductHero();

  useEffect(() => {
    openQuizModalWithTimeout(timeoutBeforeQuiz);
  }, [openQuizModalWithTimeout]);

  useEffect(() => {
    const encodedAffParam = new URL(location).searchParams.get("aff");
    const encodedAffSourceParam = new URL(location).searchParams.get("affSource");
    let decodedAffParam = null;
    let decodedAffSourceParam = null;

    if (encodedAffParam) {
      try {
        decodedAffParam = atob(encodedAffParam);
      } catch (e) {
        console.log(e);
        decodedAffParam = null;
      }
    }

    if (encodedAffSourceParam) {
      try {
        decodedAffSourceParam = atob(encodedAffSourceParam);
      } catch (e) {
        decodedAffSourceParam = null;
      }
    }

    const affCookie = getCookie("affiliate_tag");
    const affiliateTag = decodedAffParam || affCookie;

    if (window?.postId) {
      productData.get({
        postId: window.postId,
        isMobile: isMobile,
        affParam: affiliateTag,
        affSourceParam: decodedAffSourceParam,
        method: "get",
      });
    }
  }, [isMobile]);

  useEffect(() => {
    function updateIsMobile() {
      setIsMobile(window.innerWidth <= 767);
      setIsMobileGallery(window.innerWidth < 1072);
    }

    window.addEventListener("resize", updateIsMobile);
    updateIsMobile();
    return () => {
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      setLoadingState(false);
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  const enableVariants = productData?.enable_color_variants || productData?.enable_size_variants;
  const isOrderSummaryPlacementAtTop = orderSummaryVersionV2 && orderSummaryPlacement === "atTop";
  const isOrderSummaryPlacementBelowComplete = orderSummaryVersionV2 && orderSummaryPlacement === "belowComplete";

  return (
    <>
      {base.checkout_v4_topbar?.checkout_v4_topbar_show && <TopbarOffer />}
      <Header />
      {isQuizModalOpen && base.checkout_v4_quiz.quiz_show && <Quiz onClose={closeQuizModal} />}

      {isProductHeroVisible && <ProductHeroSection />}
      {isProductHeroVisible && <MobileSectionSeperator margin="0px 0px 0px 0px" />}
      <main className="main" data-cy="cc-v4">
        <div className="main_leftSide"></div>
        <section className="contentContainer">
          <article
            className={`productContainer ${isOrderSummaryPlacementAtTop && "productContainer__summaryV2"} ${base.checkout_v4_meta_bars.checkout_v4_meta_bars_show || "v2"} ${productPickerV2 && "productContainer__productPickerV2"}`}
          >
            {isOrderSummaryPlacementAtTop && isMobile && !isProductHeroVisible && (
              <div style={{ marginBottom: "8px", marginTop: "8px" }}>
                <OrderOverviewDrawerV3 />
              </div>
            )}
            {base.checkout_v4_meta_bars?.checkout_v4_meta_bars_show && (!isMobile || !isProductHeroVisible) && <Bars />}
            {productData?.variants_picker_type == "separate" && enableVariants && (
              <>
                <VariantsPickerSeparate />
                <MobileSectionSeperator margin="0px 0px 0px -16px" />
              </>
            )}
            {withProductPicker && <ProductPackages />}
            {showPremiumShipping && <PremiumShippingUpsell />}
            {base.checkout_v4_meta_bars.checkout_v4_meta_bars_show || isProductHeroVisible ? (
              <MobileSectionSeperator margin="0px 0px 24px -16px" />
            ) : (
              ""
            )}
            <Form />
            {!isMobile && <SimpleFooter />}
          </article>
          <div className={`summaryContainer v2 ${productPickerV2 && "summaryContainer__productPickerV2"}`}>
            {showMoneyBackBadge && <MoneyBackBadge />}
            {!(isMobile && orderSummaryVersionV2 && !isOrderSummaryPlacementBelowComplete) && <OrderOverviewDrawerV3 />}
            <BenefitsListV2 />
            {!complianceMode && <TestimonialsList />}
          </div>
          {isMobile && <SimpleFooter />}
        </section>
        <div className="main_rightSide"></div>
      </main>

      {((upsellPopup?.popupClicked && !upsellPopup?.popupClicked) || isLoading) && (
        <Overlay loading={isLoading} paymentMethod={paymentMethod} />
      )}
      {upsellPopup?.displayPopup && upsellPopup?.popupEnabled && (
        <UpsellPopupOverlay>
          <UpsellPopup />
        </UpsellPopupOverlay>
      )}
      {freeShippingApplied && freeShippingEnabled && <FreeShippingPopup />}
    </>
  );
};

export { Layout };
